/**
 * PatientLoader - Carga y gestión de casos de pacientes
 * Adapta datos JSON a estructuras FHIR/ClinicalNode
 */

import {
  PatientGraph,
  ClinicalNodeData,
  NodeType,
} from '../types/medical';
import { SNOMED_CT_SYSTEM, LOINC_SYSTEM } from '../types/fhir';
import { NODE_TYPE_COLORS } from '../core/config';

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export class PatientLoader {
  private cache: Map<string, PatientGraph> = new Map();

  /**
   * Carga un paciente desde URL o datos en bruto
   */
  async loadPatient(source: string | object): Promise<PatientGraph> {
    // Si es string, intentar cargar desde URL
    let data: PatientGraph;
    
    if (typeof source === 'string') {
      // Verificar cache
      if (this.cache.has(source)) {
        return this.cache.get(source)!;
      }
      
      const response = await fetch(source);
      if (!response.ok) {
        throw new Error(`Failed to load patient: ${response.statusText}`);
      }
      data = await response.json();
      this.cache.set(source, data);
    } else {
      data = source as PatientGraph;
    }

    // Validar y normalizar
    return this.normalizePatient(data);
  }

  /**
   * Carga múltiples pacientes
   */
  async loadMultiple(urls: string[]): Promise<PatientGraph[]> {
    const promises = urls.map(url => this.loadPatient(url));
    return Promise.all(promises);
  }

  /**
   * Obtiene lista de pacientes disponibles
   */
  getAvailablePatients(): string[] {
    return [
      'data/pacientes/paciente_001.json',
      'data/pacientes/paciente_002.json',
      'data/pacientes/paciente_003.json',
    ];
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // NORMALIZACIÓN
  // ═════════════════════════════════════════════════════════════════════════════
  
  private normalizePatient(data: PatientGraph): PatientGraph {
    return {
      id: data.id || `PT-${Date.now()}`,
      demographics: {
        age: data.demographics?.age || 50,
        sex: data.demographics?.sex || 'other',
        weight: data.demographics?.weight,
        height: data.demographics?.height,
        bmi: data.demographics?.bmi,
      },
      triagePriority: data.triagePriority || 'minor',
      chiefComplaint: data.chiefComplaint,
      nodes: data.nodes.map(n => this.normalizeNode(n)),
      temporalSnapshots: data.temporalSnapshots,
      lastUpdated: data.lastUpdated || new Date().toISOString(),
    };
  }

  private normalizeNode(node: ClinicalNodeData): ClinicalNodeData {
    const type = node.type || 'symptom';
    
    return {
      id: node.id || `node-${Math.random().toString(36).substr(2, 9)}`,
      type,
      concept: {
        code: node.concept?.code || 'unknown',
        display: node.concept?.display || 'Unknown',
        system: node.concept?.system || SNOMED_CT_SYSTEM,
      },
      system: node.system,
      valueQuantity: node.valueQuantity,
      valueString: node.valueString,
      valueBoolean: node.valueBoolean,
      referenceRange: node.referenceRange,
      clinicalStatus: node.clinicalStatus || 'active',
      verificationStatus: node.verificationStatus,
      severity: node.severity,
      certainty: node.certainty || 0.8,
      onsetDateTime: node.onsetDateTime || new Date().toISOString(),
      recordedDate: node.recordedDate || new Date().toISOString(),
      abatementDateTime: node.abatementDateTime,
      physics: {
        mass: node.physics?.mass || this.getDefaultMass(type),
        volatility: node.physics?.volatility || 0.5,
        timeCriticality: node.physics?.timeCriticality || 0.5,
        decayHalfLife: node.physics?.decayHalfLife,
      },
      visualization: {
        baseColor: node.visualization?.baseColor || NODE_TYPE_COLORS[type],
        priorityColor: node.visualization?.priorityColor,
        icon: node.visualization?.icon || this.getDefaultIcon(type),
        pulse: node.visualization?.pulse || node.severity === 'life-threatening',
        glowIntensity: node.visualization?.glowIntensity || 0.4,
        scale: node.visualization?.scale || 1,
      },
      relationships: node.relationships || [],
      note: node.note,
      performer: node.performer,
    };
  }

  private getDefaultMass(type: NodeType): number {
    const masses: Record<NodeType, number> = {
      vital_sign: 10,
      laboratory: 10,
      imaging: 8,
      diagnosis: 5,
      treatment: 3,
      symptom: 3,
      physical_exam: 4,
      complaint: 6,
      history: 2,
      allergy: 3,
    };
    return masses[type] || 5;
  }

  private getDefaultIcon(type: NodeType): string {
    const icons: Record<NodeType, string> = {
      vital_sign: '💓',
      laboratory: '🧪',
      imaging: '📷',
      diagnosis: '🔍',
      treatment: '💊',
      symptom: '⚠️',
      physical_exam: '👋',
      complaint: '📋',
      history: '📚',
      allergy: '🚫',
    };
    return icons[type] || '●';
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // CREACIÓN DE CASOS DE PRUEBA
  // ═════════════════════════════════════════════════════════════════════════════
  
  createIAMCase(): PatientGraph {
    // Helper local para reducir boilerplate en 18 nodos del caso.
    const mkPhysics = (mass = 5): { mass: number; volatility: number; timeCriticality: number } =>
      ({ mass, volatility: 0.2, timeCriticality: 0.7 });
    const mkViz = (color: string, icon?: string, pulse = false): { baseColor: string; icon?: string; pulse?: boolean } =>
      ({ baseColor: color, icon, pulse });

    return {
      id: 'PT-2024-001',
      demographics: { age: 67, sex: 'male', weight: 78, height: 172 },
      triagePriority: 'immediate',
      chiefComplaint: 'Dolor torácico opresivo retroesternal de 40 minutos, irradiación a brazo izquierdo y mandíbula, con diaforesis y náuseas. Inicio en reposo.',
      nodes: [
        // ── Síntomas (3) ─────────────────────────────────────────────
        {
          id: 'sx-dolor', type: 'symptom',
          concept: { code: '279035001', display: 'Dolor torácico opresivo', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: '8/10, retroesternal, irradiado a brazo izq y mandíbula',
          severity: 'severe', onsetDateTime: '2024-01-15T08:30:00Z',
          clinicalStatus: 'active', physics: mkPhysics(3), visualization: mkViz('#EF4444', '💔', true),
          relationships: [
            { targetId: 'vital-fc', type: 'associated-with', strength: 0.9, bidirectional: true },
            { targetId: 'lab-trop', type: 'indicates', strength: 0.95, bidirectional: false },
            { targetId: 'dx-iam', type: 'indicates', strength: 0.9, bidirectional: false },
          ],
        },
        {
          id: 'sx-disnea', type: 'symptom',
          concept: { code: '267036007', display: 'Disnea', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Progresiva, de pequeños esfuerzos', severity: 'moderate',
          clinicalStatus: 'active', physics: mkPhysics(3), visualization: mkViz('#EC4899', '😮‍💨'),
          relationships: [
            { targetId: 'vital-spo2', type: 'associated-with', strength: 0.7, bidirectional: true },
            { targetId: 'dx-iam', type: 'indicates', strength: 0.6, bidirectional: false },
          ],
        },
        {
          id: 'sx-diaforesis', type: 'symptom',
          concept: { code: '415690000', display: 'Diaforesis profusa', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: 'Fría, abundante', severity: 'moderate',
          clinicalStatus: 'active', physics: mkPhysics(3), visualization: mkViz('#EC4899', '💧'),
          relationships: [
            { targetId: 'dx-iam', type: 'associated-with', strength: 0.7, bidirectional: false },
          ],
        },
        // ── Vitales (6) ──────────────────────────────────────────────
        {
          id: 'vital-fc', type: 'vital_sign',
          concept: { code: '8867-4', display: 'Frecuencia cardíaca', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 110, unit: 'bpm' },
          referenceRange: { low: 60, high: 100 },
          clinicalStatus: 'active', physics: mkPhysics(10), visualization: mkViz('#EF4444', '💓', true),
          relationships: [
            { targetId: 'dx-iam', type: 'associated-with', strength: 0.5, bidirectional: false },
          ],
        },
        {
          id: 'vital-pas', type: 'vital_sign',
          concept: { code: '8480-6', display: 'PA sistólica', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 90, unit: 'mmHg' },
          referenceRange: { low: 90, high: 140 },
          clinicalStatus: 'active', physics: mkPhysics(10), visualization: mkViz('#EF4444', '🩺'),
          relationships: [
            { targetId: 'dx-iam', type: 'associated-with', strength: 0.7, bidirectional: false },
          ],
        },
        {
          id: 'vital-pad', type: 'vital_sign',
          concept: { code: '8462-4', display: 'PA diastólica', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 60, unit: 'mmHg' },
          referenceRange: { low: 60, high: 90 },
          clinicalStatus: 'active', physics: mkPhysics(10), visualization: mkViz('#EF4444'),
          relationships: [],
        },
        {
          id: 'vital-spo2', type: 'vital_sign',
          concept: { code: '59408-5', display: 'SpO₂', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 94, unit: '%' },
          referenceRange: { low: 95, high: 100 },
          clinicalStatus: 'active', physics: mkPhysics(10), visualization: mkViz('#EF4444', '🫁'),
          relationships: [],
        },
        {
          id: 'vital-fr', type: 'vital_sign',
          concept: { code: '9279-1', display: 'Frecuencia respiratoria', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 22, unit: 'rpm' },
          referenceRange: { low: 12, high: 20 },
          clinicalStatus: 'active', physics: mkPhysics(10), visualization: mkViz('#EF4444'),
          relationships: [],
        },
        {
          id: 'vital-temp', type: 'vital_sign',
          concept: { code: '8310-5', display: 'Temperatura', system: LOINC_SYSTEM },
          system: 'infectious',
          valueQuantity: { value: 36.8, unit: '°C' },
          referenceRange: { low: 36, high: 37.5 },
          clinicalStatus: 'active', physics: mkPhysics(10), visualization: mkViz('#EF4444'),
          relationships: [],
        },
        // ── Laboratorio (4) ──────────────────────────────────────────
        {
          id: 'lab-trop', type: 'laboratory',
          concept: { code: '6598-7', display: 'Troponina T', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 850, unit: 'ng/L', comparator: '>' },
          referenceRange: { high: 14 },
          clinicalStatus: 'active', severity: 'severe',
          physics: { mass: 10, volatility: 0, timeCriticality: 1.0 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪', pulse: true, glowIntensity: 0.8 },
          relationships: [
            { targetId: 'dx-iam', type: 'indicates', strength: 0.95, bidirectional: false },
          ],
        },
        {
          id: 'lab-ckmb', type: 'laboratory',
          concept: { code: '13969-1', display: 'CK-MB', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 42, unit: 'ng/mL' },
          referenceRange: { high: 5 },
          clinicalStatus: 'active', severity: 'severe',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6', '🧪'),
          relationships: [
            { targetId: 'dx-iam', type: 'indicates', strength: 0.85, bidirectional: false },
          ],
        },
        {
          id: 'lab-creat', type: 'laboratory',
          concept: { code: '2160-0', display: 'Creatinina', system: LOINC_SYSTEM },
          system: 'renal',
          valueQuantity: { value: 1.1, unit: 'mg/dL' },
          referenceRange: { low: 0.6, high: 1.3 },
          clinicalStatus: 'active', physics: mkPhysics(10), visualization: mkViz('#8B5CF6'),
          relationships: [],
        },
        {
          id: 'lab-gluc', type: 'laboratory',
          concept: { code: '2339-0', display: 'Glicemia capilar', system: LOINC_SYSTEM },
          system: 'metabolic',
          valueQuantity: { value: 165, unit: 'mg/dL' },
          referenceRange: { low: 70, high: 140 },
          clinicalStatus: 'active', severity: 'mild',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6'),
          relationships: [
            { targetId: 'hist-dm', type: 'associated-with', strength: 0.8, bidirectional: false },
          ],
        },
        // ── Imagen (1) ───────────────────────────────────────────────
        {
          id: 'img-ecg', type: 'imaging',
          concept: { code: '11524-6', display: 'ECG 12 derivaciones', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueString: 'Supradesnivel ST ≥2mm en DII, DIII y aVF. Onda Q patológica en DIII.',
          severity: 'severe', clinicalStatus: 'active',
          physics: { mass: 8, volatility: 0.1, timeCriticality: 1.0 },
          visualization: { baseColor: '#06B6D4', icon: '📷', pulse: true, glowIntensity: 0.7 },
          relationships: [
            { targetId: 'dx-iam', type: 'indicates', strength: 0.98, bidirectional: false },
          ],
        },
        // ── Antecedentes (3) ─────────────────────────────────────────
        {
          id: 'hist-hta', type: 'history',
          concept: { code: '38341003', display: 'Hipertensión arterial', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: '15 años de evolución, en tratamiento con enalapril',
          clinicalStatus: 'active', physics: mkPhysics(5), visualization: mkViz('#6B7280', '📋'),
          relationships: [
            { targetId: 'dx-iam', type: 'causes', strength: 0.6, bidirectional: false },
          ],
        },
        {
          id: 'hist-dm', type: 'history',
          concept: { code: '44054006', display: 'Diabetes Mellitus tipo 2', system: SNOMED_CT_SYSTEM },
          system: 'endocrine',
          valueString: '10 años, en metformina 850mg/12h. HbA1c reciente 8.2%',
          clinicalStatus: 'active', physics: mkPhysics(5), visualization: mkViz('#6B7280', '📋'),
          relationships: [
            { targetId: 'dx-iam', type: 'causes', strength: 0.65, bidirectional: false },
          ],
        },
        {
          id: 'hist-tabaco', type: 'history',
          concept: { code: '77176002', display: 'Tabaquismo activo', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: '30 paquetes/año, fuma actualmente 15 cig/día',
          clinicalStatus: 'active', physics: mkPhysics(5), visualization: mkViz('#6B7280'),
          relationships: [
            { targetId: 'dx-iam', type: 'causes', strength: 0.7, bidirectional: false },
          ],
        },
        // ── Diagnóstico (1) ──────────────────────────────────────────
        {
          id: 'dx-iam', type: 'diagnosis',
          concept: { code: '57054005', display: 'IAM con elevación del ST (STEMI)', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          severity: 'life-threatening',
          clinicalStatus: 'active', verificationStatus: 'provisional',
          physics: { mass: 5, volatility: 0.2, timeCriticality: 1.0 },
          visualization: { baseColor: '#F59E0B', icon: '🎯', glowIntensity: 0.8 },
          relationships: [],
        },
      ],
      lastUpdated: new Date().toISOString(),
    };
  }

  createSepsisCase(): PatientGraph {
    const mkPhysics = (mass = 5): { mass: number; volatility: number; timeCriticality: number } =>
      ({ mass, volatility: 0.2, timeCriticality: 0.8 });
    const mkViz = (color: string, icon?: string, pulse = false): { baseColor: string; icon?: string; pulse?: boolean } =>
      ({ baseColor: color, icon, pulse });

    return {
      id: 'PT-2024-002',
      demographics: { age: 45, sex: 'female', weight: 65, height: 165 },
      triagePriority: 'immediate',
      chiefComplaint: 'Fiebre alta de 48h + confusión progresiva en las últimas 12h. Disuria, polaquiuria y dolor lumbar derecho referido hace 3 días.',
      nodes: [
        // ── Síntomas (4) ─────────────────────────────────────────────
        {
          id: 'sx-fiebre', type: 'symptom',
          concept: { code: '386661006', display: 'Fiebre', system: SNOMED_CT_SYSTEM },
          system: 'infectious',
          valueString: 'Continua, de predominio vespertino, sin escalofríos',
          severity: 'severe', clinicalStatus: 'active',
          physics: mkPhysics(3), visualization: mkViz('#EF4444', '🌡️', true),
          relationships: [
            { targetId: 'vital-temp', type: 'indicates', strength: 0.95, bidirectional: false },
            { targetId: 'dx-sepsis', type: 'indicates', strength: 0.8, bidirectional: false },
          ],
        },
        {
          id: 'sx-confusion', type: 'symptom',
          concept: { code: '40917007', display: 'Confusión mental', system: SNOMED_CT_SYSTEM },
          system: 'neurological',
          valueString: 'Desorientada en tiempo y espacio. Glasgow 13 (O3 V4 M6)',
          severity: 'severe', clinicalStatus: 'active',
          physics: mkPhysics(3), visualization: mkViz('#EC4899', '🧠'),
          relationships: [
            { targetId: 'dx-sepsis', type: 'indicates', strength: 0.85, bidirectional: false },
          ],
        },
        {
          id: 'sx-disuria', type: 'symptom',
          concept: { code: '49650001', display: 'Disuria', system: SNOMED_CT_SYSTEM },
          system: 'renal',
          valueString: 'Dolorosa, urgente, polaquiuria hace 3 días',
          severity: 'moderate', clinicalStatus: 'active',
          physics: mkPhysics(3), visualization: mkViz('#EC4899'),
          relationships: [
            { targetId: 'dx-sepsis', type: 'causes', strength: 0.75, bidirectional: false },
          ],
        },
        {
          id: 'sx-lumbalgia', type: 'symptom',
          concept: { code: '279039007', display: 'Dolor lumbar derecho', system: SNOMED_CT_SYSTEM },
          system: 'renal',
          valueString: 'Puño-percusión + en fosa lumbar derecha',
          severity: 'moderate', clinicalStatus: 'active',
          physics: mkPhysics(3), visualization: mkViz('#EC4899'),
          relationships: [],
        },
        // ── Vitales (6) ──────────────────────────────────────────────
        {
          id: 'vital-temp', type: 'vital_sign',
          concept: { code: '8310-5', display: 'Temperatura', system: LOINC_SYSTEM },
          system: 'infectious',
          valueQuantity: { value: 39.2, unit: '°C' },
          referenceRange: { low: 36, high: 37.5 },
          clinicalStatus: 'active', severity: 'severe',
          physics: mkPhysics(10), visualization: mkViz('#EF4444', '🌡️', true),
          relationships: [],
        },
        {
          id: 'vital-pas', type: 'vital_sign',
          concept: { code: '8480-6', display: 'PA sistólica', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 85, unit: 'mmHg' },
          referenceRange: { low: 90, high: 140 },
          clinicalStatus: 'active', severity: 'life-threatening',
          physics: mkPhysics(10), visualization: mkViz('#DC2626', '🩺', true),
          relationships: [
            { targetId: 'dx-sepsis', type: 'indicates', strength: 0.9, bidirectional: false },
          ],
        },
        {
          id: 'vital-pad', type: 'vital_sign',
          concept: { code: '8462-4', display: 'PA diastólica', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 55, unit: 'mmHg' },
          referenceRange: { low: 60, high: 90 },
          clinicalStatus: 'active', physics: mkPhysics(10), visualization: mkViz('#DC2626'),
          relationships: [],
        },
        {
          id: 'vital-fc', type: 'vital_sign',
          concept: { code: '8867-4', display: 'Frecuencia cardíaca', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 128, unit: 'bpm' },
          referenceRange: { low: 60, high: 100 },
          clinicalStatus: 'active', severity: 'severe',
          physics: mkPhysics(10), visualization: mkViz('#EF4444', '💓', true),
          relationships: [],
        },
        {
          id: 'vital-fr', type: 'vital_sign',
          concept: { code: '9279-1', display: 'Frecuencia respiratoria', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 26, unit: 'rpm' },
          referenceRange: { low: 12, high: 20 },
          clinicalStatus: 'active',
          physics: mkPhysics(10), visualization: mkViz('#EF4444'),
          relationships: [
            { targetId: 'dx-sepsis', type: 'indicates', strength: 0.7, bidirectional: false },
          ],
        },
        {
          id: 'vital-spo2', type: 'vital_sign',
          concept: { code: '59408-5', display: 'SpO₂', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 92, unit: '%' },
          referenceRange: { low: 95, high: 100 },
          clinicalStatus: 'active',
          physics: mkPhysics(10), visualization: mkViz('#EF4444'),
          relationships: [],
        },
        // ── Laboratorio (5) ──────────────────────────────────────────
        {
          id: 'lab-lactato', type: 'laboratory',
          concept: { code: '2524-7', display: 'Lactato', system: LOINC_SYSTEM },
          system: 'metabolic',
          valueQuantity: { value: 4.2, unit: 'mmol/L' },
          referenceRange: { low: 0.5, high: 2.2 },
          clinicalStatus: 'active', severity: 'life-threatening',
          physics: { mass: 10, volatility: 0, timeCriticality: 1.0 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪', pulse: true, glowIntensity: 0.9 },
          relationships: [
            { targetId: 'dx-sepsis', type: 'indicates', strength: 0.95, bidirectional: false },
          ],
        },
        {
          id: 'lab-wbc', type: 'laboratory',
          concept: { code: '6690-2', display: 'Leucocitos', system: LOINC_SYSTEM },
          system: 'infectious',
          valueQuantity: { value: 18.5, unit: 'x10³/µL' },
          referenceRange: { low: 4, high: 11 },
          clinicalStatus: 'active', severity: 'severe',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6', '🧪'),
          relationships: [
            { targetId: 'dx-sepsis', type: 'indicates', strength: 0.85, bidirectional: false },
          ],
        },
        {
          id: 'lab-pcr', type: 'laboratory',
          concept: { code: '1988-5', display: 'PCR', system: LOINC_SYSTEM },
          system: 'infectious',
          valueQuantity: { value: 220, unit: 'mg/L' },
          referenceRange: { low: 0, high: 5 },
          clinicalStatus: 'active', severity: 'severe',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6'),
          relationships: [
            { targetId: 'dx-sepsis', type: 'indicates', strength: 0.8, bidirectional: false },
          ],
        },
        {
          id: 'lab-creat', type: 'laboratory',
          concept: { code: '2160-0', display: 'Creatinina', system: LOINC_SYSTEM },
          system: 'renal',
          valueQuantity: { value: 1.9, unit: 'mg/dL' },
          referenceRange: { low: 0.6, high: 1.3 },
          clinicalStatus: 'active', severity: 'moderate',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6'),
          relationships: [
            { targetId: 'dx-sepsis', type: 'associated-with', strength: 0.7, bidirectional: false },
          ],
        },
        {
          id: 'lab-orina', type: 'laboratory',
          concept: { code: '5804-0', display: 'Sedimento urinario', system: LOINC_SYSTEM },
          system: 'renal',
          valueString: 'Piuria (>100 leucocitos/campo), nitritos positivos, bacteriuria +++',
          clinicalStatus: 'active', severity: 'severe',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6'),
          relationships: [
            { targetId: 'dx-sepsis', type: 'causes', strength: 0.85, bidirectional: false },
          ],
        },
        // ── Antecedentes (2) ─────────────────────────────────────────
        {
          id: 'hist-itu', type: 'history',
          concept: { code: '68566005', display: 'ITU recurrente', system: SNOMED_CT_SYSTEM },
          system: 'renal',
          valueString: '3 episodios en los últimos 12 meses',
          clinicalStatus: 'active', physics: mkPhysics(5), visualization: mkViz('#6B7280', '📋'),
          relationships: [
            { targetId: 'dx-sepsis', type: 'causes', strength: 0.6, bidirectional: false },
          ],
        },
        {
          id: 'hist-dm', type: 'history',
          concept: { code: '44054006', display: 'Diabetes Mellitus tipo 2', system: SNOMED_CT_SYSTEM },
          system: 'endocrine',
          valueString: '8 años, HbA1c reciente 7.8%',
          clinicalStatus: 'active', physics: mkPhysics(5), visualization: mkViz('#6B7280'),
          relationships: [
            { targetId: 'dx-sepsis', type: 'causes', strength: 0.55, bidirectional: false },
          ],
        },
        // ── Diagnóstico (1) ──────────────────────────────────────────
        {
          id: 'dx-sepsis', type: 'diagnosis',
          concept: { code: '76571007', display: 'Shock séptico (foco urinario)', system: SNOMED_CT_SYSTEM },
          system: 'infectious',
          severity: 'life-threatening',
          clinicalStatus: 'active', verificationStatus: 'provisional',
          physics: { mass: 5, volatility: 0.2, timeCriticality: 1.0 },
          visualization: { baseColor: '#DC2626', icon: '🎯', glowIntensity: 0.9 },
          relationships: [],
        },
      ],
      lastUpdated: new Date().toISOString(),
    };
  }

  createEPOCCase(): PatientGraph {
    const mkPhysics = (mass = 5): { mass: number; volatility: number; timeCriticality: number } =>
      ({ mass, volatility: 0.3, timeCriticality: 0.7 });
    const mkViz = (color: string, icon?: string, pulse = false): { baseColor: string; icon?: string; pulse?: boolean } =>
      ({ baseColor: color, icon, pulse });

    return {
      id: 'PT-2024-003',
      demographics: { age: 72, sex: 'male', weight: 70, height: 168 },
      triagePriority: 'delayed',
      chiefComplaint: 'Disnea progresiva de 3 días sobre su basal, tos con expectoración purulenta amarillo-verdosa, uso de músculos accesorios, habla entrecortada.',
      nodes: [
        // ── Síntomas (3) ─────────────────────────────────────────────
        {
          id: 'sx-disnea', type: 'symptom',
          concept: { code: '267036007', display: 'Disnea de reposo', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'mMRC 4 (al hablar), uso de músculos accesorios',
          severity: 'severe', clinicalStatus: 'active',
          physics: mkPhysics(3), visualization: mkViz('#EC4899', '😮‍💨', true),
          relationships: [
            { targetId: 'dx-epoc', type: 'indicates', strength: 0.9, bidirectional: false },
            { targetId: 'vital-spo2', type: 'associated-with', strength: 0.85, bidirectional: true },
          ],
        },
        {
          id: 'sx-tos', type: 'symptom',
          concept: { code: '49727002', display: 'Tos productiva', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Expectoración purulenta amarillo-verdosa, abundante',
          severity: 'moderate', clinicalStatus: 'active',
          physics: mkPhysics(3), visualization: mkViz('#EC4899'),
          relationships: [
            { targetId: 'dx-epoc', type: 'indicates', strength: 0.8, bidirectional: false },
          ],
        },
        {
          id: 'sx-sibilancias', type: 'symptom',
          concept: { code: '56018004', display: 'Sibilancias bilaterales', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Audibles a distancia, espiración prolongada',
          severity: 'severe', clinicalStatus: 'active',
          physics: mkPhysics(3), visualization: mkViz('#EC4899'),
          relationships: [],
        },
        // ── Vitales (6) ──────────────────────────────────────────────
        {
          id: 'vital-fr', type: 'vital_sign',
          concept: { code: '9279-1', display: 'Frecuencia respiratoria', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 32, unit: 'rpm' },
          referenceRange: { low: 12, high: 20 },
          clinicalStatus: 'active', severity: 'severe',
          physics: mkPhysics(10), visualization: mkViz('#EF4444', '🫁', true),
          relationships: [],
        },
        {
          id: 'vital-spo2', type: 'vital_sign',
          concept: { code: '59408-5', display: 'SpO₂ (aire ambiente)', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 84, unit: '%' },
          referenceRange: { low: 95, high: 100 },
          clinicalStatus: 'active', severity: 'life-threatening',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 1.0 },
          visualization: { baseColor: '#DC2626', icon: '🫁', pulse: true, glowIntensity: 0.8 },
          relationships: [
            { targetId: 'dx-epoc', type: 'indicates', strength: 0.9, bidirectional: false },
          ],
        },
        {
          id: 'vital-fc', type: 'vital_sign',
          concept: { code: '8867-4', display: 'Frecuencia cardíaca', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 118, unit: 'bpm' },
          referenceRange: { low: 60, high: 100 },
          clinicalStatus: 'active',
          physics: mkPhysics(10), visualization: mkViz('#EF4444', '💓'),
          relationships: [],
        },
        {
          id: 'vital-pas', type: 'vital_sign',
          concept: { code: '8480-6', display: 'PA sistólica', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 145, unit: 'mmHg' },
          referenceRange: { low: 90, high: 140 },
          clinicalStatus: 'active',
          physics: mkPhysics(10), visualization: mkViz('#EF4444'),
          relationships: [],
        },
        {
          id: 'vital-temp', type: 'vital_sign',
          concept: { code: '8310-5', display: 'Temperatura', system: LOINC_SYSTEM },
          system: 'infectious',
          valueQuantity: { value: 38.1, unit: '°C' },
          referenceRange: { low: 36, high: 37.5 },
          clinicalStatus: 'active', severity: 'moderate',
          physics: mkPhysics(10), visualization: mkViz('#EF4444', '🌡️'),
          relationships: [
            { targetId: 'dx-epoc', type: 'associated-with', strength: 0.75, bidirectional: false },
          ],
        },
        {
          id: 'vital-glasgow', type: 'vital_sign',
          concept: { code: '9269-2', display: 'Escala de Glasgow', system: LOINC_SYSTEM },
          system: 'neurological',
          valueQuantity: { value: 14, unit: 'pts' },
          referenceRange: { low: 15, high: 15 },
          clinicalStatus: 'active',
          physics: mkPhysics(10), visualization: mkViz('#F59E0B'),
          relationships: [],
        },
        // ── Laboratorio (4) ──────────────────────────────────────────
        {
          id: 'lab-gasometria', type: 'laboratory',
          concept: { code: '2744-1', display: 'pH arterial', system: LOINC_SYSTEM },
          system: 'metabolic',
          valueQuantity: { value: 7.28, unit: '' },
          referenceRange: { low: 7.35, high: 7.45 },
          clinicalStatus: 'active', severity: 'severe',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6', '🧪', true),
          relationships: [
            { targetId: 'dx-epoc', type: 'indicates', strength: 0.9, bidirectional: false },
          ],
        },
        {
          id: 'lab-pco2', type: 'laboratory',
          concept: { code: '2019-8', display: 'PaCO₂', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 68, unit: 'mmHg' },
          referenceRange: { low: 35, high: 45 },
          clinicalStatus: 'active', severity: 'life-threatening',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6', '🧪'),
          relationships: [
            { targetId: 'dx-epoc', type: 'indicates', strength: 0.95, bidirectional: false },
          ],
        },
        {
          id: 'lab-hco3', type: 'laboratory',
          concept: { code: '2028-9', display: 'Bicarbonato (HCO₃)', system: LOINC_SYSTEM },
          system: 'metabolic',
          valueQuantity: { value: 32, unit: 'mmol/L' },
          referenceRange: { low: 22, high: 29 },
          clinicalStatus: 'active',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6'),
          relationships: [],
        },
        {
          id: 'lab-wbc', type: 'laboratory',
          concept: { code: '6690-2', display: 'Leucocitos', system: LOINC_SYSTEM },
          system: 'infectious',
          valueQuantity: { value: 14.2, unit: 'x10³/µL' },
          referenceRange: { low: 4, high: 11 },
          clinicalStatus: 'active', severity: 'moderate',
          physics: mkPhysics(10), visualization: mkViz('#8B5CF6'),
          relationships: [],
        },
        // ── Imagen (1) ───────────────────────────────────────────────
        {
          id: 'img-rx', type: 'imaging',
          concept: { code: '36643-5', display: 'Radiografía de tórax', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueString: 'Hiperinsuflación pulmonar, horizontalización costal, silueta cardíaca en gota. Infiltrado parcheado en base derecha sugiere sobreinfección bacteriana.',
          severity: 'moderate', clinicalStatus: 'active',
          physics: mkPhysics(8), visualization: mkViz('#06B6D4', '📷'),
          relationships: [
            { targetId: 'dx-epoc', type: 'indicates', strength: 0.85, bidirectional: false },
          ],
        },
        // ── Antecedentes (3) ─────────────────────────────────────────
        {
          id: 'hist-epoc', type: 'history',
          concept: { code: '13645005', display: 'EPOC GOLD III', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: '10 años, FEV₁ 42% basal. 2 exacerbaciones en el último año',
          clinicalStatus: 'active', severity: 'severe',
          physics: mkPhysics(5), visualization: mkViz('#6B7280', '📋'),
          relationships: [
            { targetId: 'dx-epoc', type: 'causes', strength: 0.95, bidirectional: false },
          ],
        },
        {
          id: 'hist-tabaco', type: 'history',
          concept: { code: '77176002', display: 'Tabaquismo crónico', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: '50 paquetes/año, fuma actualmente 20 cig/día',
          clinicalStatus: 'active', physics: mkPhysics(5), visualization: mkViz('#6B7280'),
          relationships: [
            { targetId: 'dx-epoc', type: 'causes', strength: 0.9, bidirectional: false },
          ],
        },
        {
          id: 'hist-ic', type: 'history',
          concept: { code: '42343007', display: 'Insuficiencia cardíaca', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: 'FEVI 45%, NYHA II. En enalapril + carvedilol',
          clinicalStatus: 'active', physics: mkPhysics(5), visualization: mkViz('#6B7280'),
          relationships: [],
        },
        // ── Diagnóstico (1) ──────────────────────────────────────────
        {
          id: 'dx-epoc', type: 'diagnosis',
          concept: { code: '195967001', display: 'EPOC exacerbado con insuficiencia respiratoria tipo II', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          severity: 'severe',
          clinicalStatus: 'active', verificationStatus: 'provisional',
          physics: { mass: 5, volatility: 0.2, timeCriticality: 0.9 },
          visualization: { baseColor: '#F59E0B', icon: '🎯', glowIntensity: 0.8 },
          relationships: [],
        },
      ],
      lastUpdated: new Date().toISOString(),
    };
  }
}

export default PatientLoader;
